import { SyntheticEvent, useRef, useState } from "react"
import Image from "next/image"
import { usePathname } from "next/navigation"
import { cn } from "cn"

import { CF_S3_URL } from "@/lib/cloudflare-loader"
import { Container } from "@/components/container"
import { Button } from "@/components/ui/button"
import { Form, FormControl, FormField, FormLabel } from "@/components/ui/form"
import { Link } from "@/components/ui/link"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { TextInput } from "@/components/ui/text-input"
import { Textarea } from "@/components/ui/textarea"

const PRODUCT_OF_INTEREST_ITEMS: Record<string, string> = {
  destinations: "Mindtrip for Destinations",
  hotels: "Mindtrip for Hotels",
  "answer intelligence": "Answer Intelligence",
  "whatsapp integration": "WhatsApp Integration",
  other: "Other",
}

const SOURCE_ITEMS: Record<string, string> = {
  agency_partner: "Agency partner",
  digital_ad: "Digital ad",
  email_marketing: "Email marketing",
  news_publication: "News publication",
  search_engine: "Search engine",
  social_media: "Social media",
  tourism_website: "Tourism website",
  trade_show: "Trade show/event",
  word_of_mouth: "Word of mouth",
  other: "Other",
}

export function BusinessContactForm() {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [source, setSource] = useState("")
  const [productOfInterest, setProductOfInterest] = useState<string[]>([])
  const formRef = useRef<HTMLFormElement>(null)
  const pathname = usePathname()

  const handleSubmit = async (e: SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (isSubmitting) return

    setIsSubmitting(true)
    const formData = new FormData(e.currentTarget)

    try {
      const response = await fetch("/api/hubspot-business-contact", {
        method: "POST",
        body: JSON.stringify({
          firstname: formData.get("firstname") || "",
          lastname: formData.get("lastname") || "",
          email: formData.get("email") || "",
          phone: formData.get("phone") || "",
          company: formData.get("company") || "",
          jobtitle: formData.get("jobtitle") || "",
          mtb_product_of_interest_multi_select: productOfInterest.join(";"),
          message: formData.get("message") || "",
          source,
        }),
        headers: {
          "Content-Type": "application/json",
        },
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.message || "Failed to submit form")
      }

      setSubmitted(true)
      formRef.current?.reset()
    } catch (error) {
      console.error("Error submitting form:", error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const isOverview = pathname === "/business"
  const isDestinations = pathname === "/business/destinations"
  const isHotels = pathname === "/business/hotels"
  const showLogos = !isOverview && !isDestinations && !isHotels

  return (
    <div id="contact" className="bg-gray-2">
      <div className="rounded-b-4xl bg-black py-24 text-white sm:rounded-b-7xl">
        <Container>
          <div className="grid items-center gap-10 bg-black text-white xl:grid-cols-2 xl:gap-16">
            <div>
              <div className="@container mx-auto flex max-w-2xl flex-col gap-5 text-center sm:text-left">
                <h2 className="font-circular text-5xl text-balance @lg:text-7xl @2xl:text-8xl">
                  See Mindtrip for Business in action.
                </h2>
                <p className="text-lg text-balance xl:max-w-[20em]">
                  Drop us your info, and we’ll answer any questions you have and
                  set up a demo.
                </p>
                {showLogos && (
                  <div className="mt-6 hidden grid-cols-3 items-center gap-9 *:flex *:items-center *:justify-center xl:grid">
                    <div>
                      <Link
                        href="https://visittheusa.com"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Image
                          src={`${CF_S3_URL}/web/logo-brand-usa-ko.svg`}
                          alt="Visit the USA"
                          width="95"
                          height="48"
                          unoptimized
                        />
                      </Link>
                    </div>
                    <div>
                      <Link
                        href="https://www.visitcostarica.com"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Image
                          src={`${CF_S3_URL}/web/costarica_logo-ko.svg`}
                          alt="esencial Costa Rica"
                          width="130"
                          height="59"
                          unoptimized
                        />
                      </Link>
                    </div>
                    <div>
                      <Link
                        href="https://www.visitcalifornia.com/road-trips"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Image
                          src={`${CF_S3_URL}/web/visit-california-logo-ko.svg`}
                          alt="Visit California"
                          width="160"
                          height="50"
                          unoptimized
                        />
                      </Link>
                    </div>
                    <div>
                      <Link
                        href="https://www.discoverpuertorico.com"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Image
                          src={`${CF_S3_URL}/web/logo-discover-puerto-rico-ko.svg`}
                          alt="esencial Costa Rica"
                          width="152"
                          height="47"
                          unoptimized
                        />
                      </Link>
                    </div>
                    <div>
                      <Link
                        href="https://www.seemonterey.com"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Image
                          src={`${CF_S3_URL}/web/biz-logo-5-ko.png`}
                          alt="Monterey County"
                          width="140"
                          height="40"
                        />
                      </Link>
                    </div>
                    <div>
                      <Link
                        href="https://www.outerbanks.org"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Image
                          src={`${CF_S3_URL}/web/logo-outer-banks.svg`}
                          alt="The Outer Banks of North Carolina"
                          width="186"
                          height="37"
                          className="invert"
                          unoptimized
                        />
                      </Link>
                    </div>
                    <div>
                      <Link
                        href="https://www.visittruckeetahoe.com"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Image
                          src={`${CF_S3_URL}/web/logo-truckee-tahoe.svg`}
                          alt="Visit Truckee Tahoe"
                          width="125"
                          height="55"
                          className="invert"
                          unoptimized
                        />
                      </Link>
                    </div>
                    <div>
                      <Link
                        href="https://travelnevada.com"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Image
                          src={`${CF_S3_URL}/web/logo-travel-nevada.svg`}
                          alt="Travel Nevada"
                          width="100"
                          height="81"
                          className="invert"
                          unoptimized
                        />
                      </Link>
                    </div>
                    <div>
                      <Link
                        href="https://www.neworleans.com"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Image
                          src={`${CF_S3_URL}/web/logo-new-orleans.svg`}
                          alt="Visit New Orleans"
                          width="180"
                          height="30"
                          className="invert"
                          unoptimized
                        />
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div>
              <div className="relative mx-auto max-w-2xl rounded-3xl bg-white p-8 text-black sm:p-10">
                <Form
                  ref={formRef}
                  onSubmit={handleSubmit}
                  className={cn(
                    "grid gap-x-4 gap-y-6 transition-opacity sm:grid-cols-2",
                    submitted && "pointer-events-none invisible"
                  )}
                >
                  <FormField name="firstname">
                    <FormLabel size={2}>First name</FormLabel>
                    <FormControl
                      render={<TextInput required autoComplete="given-name" />}
                    />
                  </FormField>
                  <FormField name="lastname">
                    <FormLabel size={2}>Last name</FormLabel>
                    <FormControl
                      render={<TextInput required autoComplete="family-name" />}
                    />
                  </FormField>
                  <FormField name="email">
                    <FormLabel size={2}>Email</FormLabel>
                    <FormControl
                      render={
                        <TextInput
                          type="email"
                          required
                          autoComplete="email"
                          maxLength={255}
                        />
                      }
                    />
                  </FormField>
                  <FormField name="phone">
                    <FormLabel size={2}>Phone</FormLabel>
                    <FormControl
                      render={<TextInput type="tel" autoComplete="tel" />}
                    />
                  </FormField>
                  <FormField name="company">
                    <FormLabel size={2}>Company</FormLabel>
                    <FormControl render={<TextInput required />} />
                  </FormField>
                  <FormField name="jobtitle">
                    <FormLabel size={2}>Title</FormLabel>
                    <FormControl render={<TextInput required />} />
                  </FormField>
                  <FormField
                    name="mtb_product_of_interest_multi_select"
                    className="sm:col-span-2"
                  >
                    <FormLabel size={2}>
                      Which product are you interested in?
                    </FormLabel>
                    <Select
                      multiple
                      value={productOfInterest}
                      onValueChange={(v) => setProductOfInterest(v ?? [])}
                      items={PRODUCT_OF_INTEREST_ITEMS}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select all that apply" />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(PRODUCT_OF_INTEREST_ITEMS).map(
                          ([value, label]) => (
                            <SelectItem key={value} value={value}>
                              {label}
                            </SelectItem>
                          )
                        )}
                      </SelectContent>
                    </Select>
                  </FormField>
                  <FormField name="source" className="sm:col-span-2">
                    <FormLabel size={2}>
                      How did you hear about Mindtrip?
                    </FormLabel>
                    <Select
                      value={source}
                      onValueChange={(v) => v != null && setSource(v)}
                      items={{
                        "": "Select an option",
                        ...SOURCE_ITEMS,
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(SOURCE_ITEMS).map(([value, label]) => (
                          <SelectItem key={value} value={value}>
                            {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormField>
                  <FormField name="message" className="sm:col-span-2">
                    <FormLabel size={2}>Message</FormLabel>
                    <FormControl render={<Textarea rows={3} />} />
                  </FormField>
                  <div>
                    <Button
                      type="submit"
                      variant="primary"
                      size="2xl"
                      loading={isSubmitting}
                    >
                      Submit
                    </Button>
                  </div>
                </Form>
                {submitted && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-5 text-center">
                    <h3 className="text-5xl font-semibold">
                      <span className="font-emoji">👋</span>
                      <br />
                      Thank you
                    </h3>
                    <p className="text-lg">We’ll be in touch shortly.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Container>
      </div>
    </div>
  )
}
